/* eslint-disable @typescript-eslint/naming-convention */
// angular
import { Inject, Injectable, InjectionToken, Optional } from '@angular/core';

export type LogTelemetryLevel = 'log' | 'info' | 'warn' | 'error';

export interface ILogTelemetrySink {
  record(level: LogTelemetryLevel, args: readonly unknown[]): void;
}

export const LOG_TELEMETRY_SINK = new InjectionToken<ILogTelemetrySink>(
  'LOG_TELEMETRY_SINK'
);

export interface IDebug {
  LEVEL_1: boolean;
  LEVEL_2: boolean;
  LEVEL_3: boolean;
  LEVEL_4: boolean;
  LEVEL_5: boolean;
  // isolate logging to specific logTag's
  ISOLATE_LOG_TAGS?: Array<string>;
  // exclude logging to specific logTag's
  EXCLUDE_LOG_TAGS?: Array<string>;
}

export interface IHttpDebug {
  enable: boolean;
  includeRequestBody?: boolean;
  includeRequestHeaders?: boolean;
  includeResponse?: boolean;
  jsonFilePath?: string; // if this is set, it will use local json files
}

@Injectable({
  providedIn: 'root',
})
export class LogService {
  constructor(
    @Optional()
    @Inject(LOG_TELEMETRY_SINK)
    private readonly telemetry?: ILogTelemetrySink
  ) {}

  static DEBUG: IDebug = {
    LEVEL_1: false, // .warn only
    LEVEL_2: false, // .error only
    LEVEL_3: false, // .log + all the above
    LEVEL_4: false, // .log + all the above + info
    LEVEL_5: false, // just info (excluding all else)
  };

  static DEBUG_HTTP: IHttpDebug = {
    enable: false,
    includeRequestBody: false,
    includeRequestHeaders: false,
    includeResponse: false,
  };

  // info (extra messages like analytics)
  // use LEVEL_5 to see only these
  info(...msg: Array<unknown>) {
    if (this.record('info', msg)) return;
    if (LogService.DEBUG.LEVEL_5 || LogService.DEBUG.LEVEL_4) {
      // extra messages
      // eslint-disable-next-line no-restricted-syntax
      console.info(msg);
    }
  }

  // debug (standard output)
  debug(...msg: Array<unknown>) {
    if (
      LogService.DEBUG.EXCLUDE_LOG_TAGS?.includes(msg[0] as string) ||
      (LogService.DEBUG.ISOLATE_LOG_TAGS &&
        !LogService.DEBUG.ISOLATE_LOG_TAGS.includes(msg[0] as string))
    ) {
      return;
    }
    if (this.record('log', msg)) return;
    if (LogService.DEBUG.LEVEL_4 || LogService.DEBUG.LEVEL_3) {
      console.log(msg);
    }
  }

  // error
  error(...err: Array<unknown>) {
    if (this.record('error', err)) return;
    if (
      LogService.DEBUG.LEVEL_4 ||
      LogService.DEBUG.LEVEL_3 ||
      LogService.DEBUG.LEVEL_2
    ) {
      console.error(err);
    }
  }

  // warn
  warn(...warn: Array<unknown>) {
    if (this.record('warn', warn)) return;
    if (
      LogService.DEBUG.LEVEL_4 ||
      LogService.DEBUG.LEVEL_3 ||
      LogService.DEBUG.LEVEL_1
    ) {
      console.warn(warn);
    }
  }

  private record(level: LogTelemetryLevel, args: readonly unknown[]): boolean {
    if (!this.telemetry) return false;
    try {
      this.telemetry.record(level, args);
    } catch {
      // Logging must never interrupt the caller.
    }
    return true;
  }
}
